import { Form, Input, message, Select, Skeleton, Upload } from 'antd';
import { find } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import Button from '../../../components/Button/Button';
import InputText from '../../../components/Input/InputText';
import { apiInstance } from '../../../utils/api';

const ContactForm = ({
  contact,
  handleInputChange,
  handleSubmit,
  handlePositionChange,
  isNewContact,
  positions,
  references,
  beforeUpload,
  handleChangePhotoUpload,
  removePhoto,
  onDismiss,
  isDismissLoading,
}) => {
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const isEditing = !isNewContact;
  const [form] = Form.useForm();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [phoneExists, setPhoneExists] = useState(false);
  const [photoError, setPhotoError] = useState(false);

  // Реф для актуального contact — щоб onFinish завжди читав свіже значення
  const contactRef = useRef(contact);
  useEffect(() => {
    contactRef.current = contact;
  }, [contact]);

  useEffect(() => {
    if (Object.keys(contact).length > 0) {
      setIsDataLoaded(true);
    }
  }, [contact]);

  useEffect(() => {
    if (isEditing && contact) {
      form.setFieldsValue({
        RespName: contact?.RespName,
        RespSurName: contact?.RespSurName,
        RespMobilePhone: contact?.RespMobilePhone,
        RespEmail: contact?.RespEmail || '',
        RespEmployeeJob: contact.RespEmployeeJobId?.displayValue || contact.Job?.displayValue || contact.Job?.value || '',
      });
    }
  }, [contact, form, isEditing]);

  const handleFieldChange = (name, value) => {
    form.setFieldsValue({ [name]: value });
    handleInputChange({ target: { name, value } });
  };

  const checkPhoneUniqueness = async (phoneNumber) => {
    if (!phoneNumber || phoneNumber.length < 13) return false;

    try {
      const response = await apiInstance(
        `${process.env.REACT_APP_API_URL}/api/crm/get-data?type=check-contact-phone`,
        'POST',
        { phoneNumber }
      );

      const exists = response.rows && response.rows.length > 0;
      setPhoneExists(exists);

      return exists;
    } catch (error) {
      console.error('Ошибка проверки телефона:', error);
      return false;
    }
  };

  const requiredFieldRule = (isNew) => ({
    required: isNew,
    message: "Обов'язкове поле",
  });

  // Правило для кирилиці — спрацює бо поля тепер ANT Input
  const cyrillicRule = {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (!/^[А-ЯҐЄІЇа-яґєії'\-\s]+$/.test(value)) {
        return Promise.reject(new Error('Дозволені лише літери кирилиці'));
      }
      return Promise.resolve();
    },
  };

  // Фізично блокуємо некирилицю при введенні
  const blockNonCyrillic = (e) => {
    const specialKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End', 'Tab', 'Enter',
    ];
    if (specialKeys.includes(e.key)) return;
    if (e.ctrlKey || e.metaKey) return;
    const cyrillicAndAllowed = /^[А-ЯҐЄІЇа-яґєії'\-\s]$/;
    if (!cyrillicAndAllowed.test(e.key)) {
      e.preventDefault();
    }
  };

  const onFinish = async (values) => {
    // Перевіряємо фото через ref — contact.attachFileName1 оновлюється асинхронно
    if (isNewContact && !contactRef.current?.attachFileName1) {
      setPhotoError(true);
      message.error('Завантажте фото співробітника');
      return;
    }
    setPhotoError(false);
    setIsButtonDisabled(true);
    try {
      const phoneAlreadyUsed = isNewContact
        ? await checkPhoneUniqueness(values.RespMobilePhone)
        : false;
      await handleSubmit(values, phoneAlreadyUsed);
    } catch (error) {
      console.error('Помилка відправки:', error);
      message.error(error.message || 'Помилка збереження');
    } finally {
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, 3000);
    }
  };

  if (isEditing && !isDataLoaded) {
    return (
      <div
        className="box"
        style={{
          gap: '40px',
          marginTop: '24px',
          position: 'relative',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <div className="contact-form-inner" style={{ gap: '20px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: '24px' }}>
              <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>Ім'я:</div>
              <Skeleton.Input active style={{ width: '267px', height: '52px', borderRadius: '6px' }} />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>Телефон:</div>
              <Skeleton.Input active style={{ width: '267px', height: '52px', borderRadius: '6px' }} />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>Email:</div>
              <Skeleton.Input active style={{ width: '267px', height: '52px', borderRadius: '6px' }} />
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: '24px' }}>
              <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>Прізвище:</div>
              <Skeleton.Input active style={{ width: '267px', height: '52px', borderRadius: '6px' }} />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>Завантажити фото (jpg, png):</div>
              <Skeleton.Input active style={{ width: '267px', height: '52px', borderRadius: '6px' }} />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>Посада:</div>
              <Skeleton.Input active style={{ width: '267px', height: '52px', borderRadius: '6px' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="box contact-form-box"
      style={{
        gap: '40px',
        marginTop: '24px',
        position: 'relative',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Form
        form={form}
        onFinish={onFinish}
        className="form field-grey-straight-border"
        style={{ display: 'flex', flexDirection: 'column' }}
        initialValues={{
          RespName: contact?.RespName || '',
          RespSurName: contact?.RespSurName || '',
          RespMobilePhone: contact?.RespMobilePhone || '',
          RespEmail: contact?.RespEmail || '',
        }}
      >
        <div className="contact-form-inner" style={{ gap: '20px', flexDirection: 'row', display: 'flex' }}>
          <div style={{ flex: 1, minWidth: 300 }}>
            {/* ІМ'Я — тільки кирилиця */}
            <Form.Item
              name="RespName"
              className="zeroMT"
              label="Ім'я:"
              validateTrigger={['onSubmit', 'onChange']}
              rules={[requiredFieldRule(isNewContact), cyrillicRule]}
              required={false}
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder={isEditing ? '' : "Введіть ім'я"}
                disabled={isEditing}
                onKeyDown={blockNonCyrillic}
                onChange={(e) => handleInputChange({ target: { name: 'RespName', value: e.target.value } })}
              />
            </Form.Item>

            {/* ТЕЛЕФОН */}
            <Form.Item
              name="RespMobilePhone"
              label="Телефон:"
              style={{ marginTop: 0, marginBottom: 16 }}
              className="contact-phone-input"
              validateTrigger={['onSubmit', 'onChange']}
              rules={[
                ...(isNewContact ? [{ required: true, message: 'Вкажіть телефон' }] : []),
                {
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.resolve();
                    }
                    if (!value.startsWith('+38')) {
                      return Promise.reject(new Error('Номер повинен починатися з +38'));
                    }
                    if (value.length < 13) {
                      return Promise.reject(new Error('Номер повинен містити 12 цифр'));
                    }
                    const operatorCode = value.substring(3, 6);
                    if (!references || !references.MobileCodes) {
                      console.warn('Відсутній довідник MobileCodes');
                      return Promise.resolve();
                    }
                    const isValidOperatorCode = find(references.MobileCodes, {
                      Code: operatorCode,
                    });
                    if (!isValidOperatorCode) {
                      return Promise.reject(new Error('Невірний код оператора'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              required={false}
            >
              <Input
                placeholder={isEditing ? '' : '+380XXXXXXXX'}
                disabled={isEditing || isButtonDisabled}
                maxLength={13}
                onKeyPress={(e) => {
                  const allowedChars = /^[0-9+]$/;
                  if (!allowedChars.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={async (e) => {
                  const nextValue = e.target.value;
                  handleFieldChange('RespMobilePhone', nextValue);
                  if (nextValue.length >= 13) {
                    await checkPhoneUniqueness(nextValue);
                  }
                }}
              />
            </Form.Item>

            {/* EMAIL */}
            <Form.Item
              name="RespEmail"
              className="zeroMT"
              label="Email:"
              rules={[
                requiredFieldRule(isNewContact),
                { type: 'email', message: 'Невірний формат email' },
              ]}
              required={false}
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder={isEditing ? '' : 'example@email.com'}
                disabled={isEditing}
                onChange={(e) => handleFieldChange('RespEmail', e.target.value)}
              />
            </Form.Item>
          </div>

          <div style={{ flex: 1, minWidth: 300 }}>
            {/* ПРІЗВИЩЕ — тільки кирилиця */}
            <Form.Item
              name="RespSurName"
              className="zeroMT"
              label="Прізвище:"
              validateTrigger={['onSubmit', 'onChange']}
              rules={[requiredFieldRule(isNewContact), cyrillicRule]}
              required={false}
              style={{ marginBottom: 16 }}
            >
              <Input
                placeholder={isEditing ? '' : 'Введіть прізвище'}
                disabled={isEditing}
                onKeyDown={blockNonCyrillic}
                onChange={(e) => handleInputChange({ target: { name: 'RespSurName', value: e.target.value } })}
              />
            </Form.Item>

            {/* ФОТО */}
            <Form.Item
              name="attach_file_1"
              className="field_col contact-photo-upload"
              colon={false}
              label={
                <span style={{ whiteSpace: 'nowrap' }}>
                  Фото (jpg, png):
                </span>
              }
              required={false}
              style={{ marginBottom: 16 }}
            >
              <Upload
                beforeUpload={(file) => {
                  // beforeUpload = handlePhotoUpload з ContactPage
                  // Він сам читає файл і оновлює contact.attachFileName1
                  beforeUpload(file);
                  setPhotoError(false);
                  return false;
                }}
                className="employee-form__upload"
                maxCount={1}
                onRemove={() => {
                  removePhoto();
                  setPhotoError(false);
                }}
                disabled={isEditing}
                style={{ cursor: isEditing ? 'default' : 'pointer' }}
                fileList={
                  contact?.attachFileName1
                    ? [
                        {
                          uid: '-1',
                          name: contact.attachFileName1,
                          status: 'done',
                        },
                      ]
                    : []
                }
              >
                {!contact?.attachFileName1 && !isEditing && (
                  <span style={{ paddingLeft: '16px', fontSize: '16px', cursor: 'pointer' }}>
                    Виберіть файл
                  </span>
                )}
                {isEditing && contact?.attachFileName1 && (
                  <span
                    style={{
                      paddingLeft: '16px',
                      fontSize: '14px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block',
                      maxWidth: '100%',
                    }}
                    title={contact.attachFileName1}
                  >
                    {contact.attachFileName1}
                  </span>
                )}
              </Upload>
              {photoError && (
                <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                  Обов'язкове поле
                </div>
              )}
            </Form.Item>

            {/* ПОСАДА */}
            <Form.Item
              colon={false}
              name="RespEmployeeJob"
              label="Посада:"
              rules={[requiredFieldRule(isNewContact)]}
              required={false}
              style={{ marginBottom: 16 }}
            >
              <Select
                value={isEditing ? (contact.RespEmployeeJobId?.displayValue || contact.Job?.displayValue || contact.RespEmployeeJobId?.value) : contact.position?.Id || undefined}
                onChange={
                  isEditing
                    ? undefined
                    : (value) => {
                        form.setFieldsValue({ RespEmployeeJob: value });
                        const selected = positions.find((option) => option.Id === value);
                        if (selected) {
                          handleInputChange({
                            target: {
                              name: 'RespEmployeeJob',
                              value: { Id: selected.Id, name: selected.Name },
                            },
                          });
                          handlePositionChange?.(selected);
                        }
                      }
                }
                options={
                  Array.isArray(positions)
                    ? positions.map((option) => ({
                        value: option.Id,
                        label: option.Name,
                      }))
                    : []
                }
                style={{ width: '100%' }}
                placeholder={isEditing ? contact.RespEmployeeJobId?.displayValue : 'Оберіть посаду'}
                disabled={isEditing}
              />
            </Form.Item>
          </div>
        </div>

        {isNewContact ? (
          <div className="btn-create-contact">
            <Button
              type="submit"
              text="Створити"
              bg="turquoise"
              block
              disabled={isButtonDisabled}
            />
          </div>
        ) : (
          !contact?.isCardOrdered && (
            <div className="btn-create-contact" style={{ marginTop: 24 }}>
              <Button
                type="button"
                text="Звільнити співробітника"
                bg="pink"
                block
                loading={isDismissLoading}
                onClick={onDismiss}
              />
            </div>
          )
        )}
      </Form>
    </div>
  );
};

export default ContactForm;
